import jwtDecode, {JwtPayload} from 'jwt-decode';
import {Credentials} from 'react-native-auth0';
import {Platform} from 'react-native';
import {getMobileAuth0Client, getWebAuth0Client} from 'src/nativeModules/Auth0';
import moment from 'moment/moment';

export default class AuthStore {
  private accountId: string | null = null;
  private email: string | null = null;
  private isSSO = false;
  private isNewUser: boolean | null = null;
  private hasQRCode: boolean | null = null;
  private webSession: Credentials | null = null;

  public async migrateCredentials(): Promise<boolean> {
    if (Platform.OS === 'web') {
      return false;
    }
    try {
      const isValid =
        await getMobileAuth0Client().credentialsManager.hasValidCredentials();
      if (isValid) {
        await this.loadInMemoryData();
        return true;
      }
      return false;
    } catch (error) {

      return false;
    }
  }

  public async storeSession(session: Credentials) {
    try {
      if (Platform.OS === 'web') {
        this.webSession = session;
      } else {
        await getMobileAuth0Client().credentialsManager.saveCredentials(
          session,
        );
      }
      this.assignValuesFromIdToken(session);
    } catch (err) {

    }
  }

  public async clearSession() {
    this.accountId = null;
    this.email = null;
    this.isSSO = false;
    this.isNewUser = null;
    this.hasQRCode = null;
    this.webSession = null;
    try {
      if (Platform.OS !== 'web') {
        await getMobileAuth0Client().credentialsManager.clearCredentials();
      }
    } catch (err) {

    }
  }

  public async getAccessToken(): Promise<string | null> {
    try {
      const creds = await this.getCredentials();
      return creds?.accessToken || null;
    } catch (error) {

      return null;
    }
  }

  public async getRefreshToken(): Promise<string | null> {
    try {
      const creds = await this.getCredentials();
      return creds?.refreshToken ?? null;
    } catch (error) {

      return null;
    }
  }

  public async getAccountId(): Promise<string | null> {
    if (!this.accountId) {
      await this.loadInMemoryData();
    }
    return this.accountId;
  }

  public async getIsSSO(): Promise<boolean> {
    if (this.isSSO == null) {
      await this.loadInMemoryData();
    }
    return this.isSSO;
  }

  public async shouldShowQRCode(): Promise<boolean> {
    if (this.hasQRCode === null || this.isNewUser === null) {
      await this.loadInMemoryData();
    }
    return !!this.hasQRCode && !!this.isNewUser;
  }

  private async loadInMemoryData() {
    try {
      const creds = await this.getCredentials();
      if (creds) {
        this.assignValuesFromIdToken(creds);
      }
    } catch (error) {

    }
  }

  private async getCredentials(): Promise<Credentials | null> {
    if (Platform.OS === 'web') {
      try {
        const client = getWebAuth0Client();
        const accessToken = await client.getTokenSilently({
          detailedResponse: true,
        });
        const idToken = await client.getIdTokenClaims();

        if (!accessToken?.access_token || !idToken?.__raw) {
          return null;
        }

        return {
          accessToken: accessToken.access_token,
          idToken: idToken.__raw,
          refreshToken: 'web',
          expiresAt: moment().unix() + accessToken.expires_in,
          tokenType: 'Bearer',
          scope: '',
          isSSO: idToken['https://365rm.us/isSSO'],
          isNewUser: idToken['https://365rm.us/isNewUser'],
          hasQRCode: idToken['https://365rm.us/hasQRCode'],
        };
      } catch (e) {

        return null;
      }
    } else {
      return await getMobileAuth0Client().credentialsManager.getCredentials();
    }
  }

  private assignValuesFromIdToken(credentials: Credentials) {
    if (!credentials.idToken) {
      return;
    }
    try {
      const data = jwtDecode<JwtPayload>(credentials.idToken) as Record<
        string,
        any
      >;
      this.accountId = data['https://365rm.us/accountId'] ?? null;
      this.email = data.email ?? null;
      this.isSSO = !!data['https://365rm.us/isSSO'];
      this.isNewUser = data['https://365rm.us/isNewUser'] ?? null;
      this.hasQRCode = data['https://365rm.us/hasQRCode'] ?? null;
    } catch (error) {

    }
  }
}
